import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import CardHeader from '@material-ui/core/CardHeader';

import CloseIcon from '@material-ui/icons/Close';
import UserIcon from '@material-ui/icons/Person';

import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

const dateTemplate = {
    id: 0,
    invitation_id: 0,
    name: ""
}

export default function EditDateDialog(props) {

    const { open, onSave, onClose, onClosed, initDate, invitation, admin } = props;

    const [ date, setDate ] = useState(dateTemplate);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();
    const api = useApi();

    // Entering Functions ------------------------------------------

    const handleEnter = () => {
        if (initDate && initDate?.id > 0) setDate(initDate);
        else setDate({...dateTemplate, invitation_id: invitation?.id})
    }

    // Handler Functions ------------------------------------------

    const handleDate = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setDate({ ...date, [name]: value });
    };

    const datesAllowed = invitation ? invitation.dates_allowed : 0;

    // Poster functions ------------------------------------------

    const postDate = () => {


        setLoading(true);

        api.post(`${admin ? '/admin/' : '/'}date/${date.id}`, date)
            .then(() => onSave())
            .catch(error => {
                setLoading(false)
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    };

    const deleteDate = () => {

        setLoading(true);

        api.delete(`${admin ? '/admin/' : '/'}date/${date.id}`)
            .then(() => onSave())
            .catch(error => {
                setLoading(false)
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    };

    // Error Handling -------------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    // Exting Functions ----------------------------------------

    const clearState = () => {
        setDate(dateTemplate);
    };

    const handleClosed = () => {
        clearState();
        setLoading(false);
        if (onClosed) onClosed();
    }

    return (
        <Dialog
            aria-labelledby="edit-date-dialog-title"
            open={open}
            aria-describedby="edit-date-dialogue-description"
            fullWidth
            maxWidth="sm"
            disableBackdropClick
            TransitionProps={{
                onExited: handleClosed,
                onEnter: handleEnter
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-date-dialog-title">
                {(date.id > 0) ? 'Edit Date' : 'Add New Date'}
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers id="edit-date-dialog-content">
                <form>
                    <Typography variant="body1" color="textSecondary" gutterBottom>
                        Add the name of a date to your invitation. Your invitation allows up to {datesAllowed} <strong>{(datesAllowed === 1) ? 'date' : 'dates'}</strong>.
                    </Typography>
                    <CardHeader
                        avatar={
                            <Avatar >
                                <UserIcon />
                            </Avatar>
                        }
                        title={
                            <>
                                <TextField
                                    id="date-name"
                                    label="Date Name"
                                    value={date.name}
                                    onChange={handleDate('name')}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    error={isError('name')}
                                    helperText={getErrorMessage('name')}
                                />
                            </>
                        }
                        subtitle="Enter date name and press the add button on the right to save."
                        className={classes.marginBottom}
                    />
                    <Typography variant="subtitle1" color="primary" gutterBottom align="right">
                        <strong>{invitation.dates.length}</strong> {(invitation.dates.length === 1) ? 'date' : 'dates'} currently added to invitation.
                    </Typography>
                </form>
            </DialogContent>
            <DialogActions>
                {(date.id > 0) &&
                    <RedButton onClick={deleteDate} disabled={false} variant="outlined">
                        Delete Date
                    </RedButton>
                }
                <Button onClick={postDate} color="primary" disabled={false} variant="contained">
                    Save Date
                </Button>
            </DialogActions>
        </Dialog>
    );
}
